/**
 * TransformTool - AI Transformation Interface
 *
 * Provides real AI-powered content transformation via NPE backends:
 * - Humanize (intensity control)
 * - Persona (16 built-in presets + custom)
 * - Style (14 built-in presets + custom)
 *
 * Preview before applying. On apply, writes to buffer and signals
 * workspace to enter split-pane mode.
 *
 * @module @humanizer/studio/components/tools/TransformTool
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useBufferSync } from '../../contexts/BufferSyncContext';
import { useApi } from '../../contexts/ApiContext';
import { useTransformApi, FALLBACK_PERSONAS, FALLBACK_STYLES, type DetectionComparison, type DetectionSignalSnapshot, type HumanizeGenre } from '../../hooks/useTransformApi';
import type { PresetInfo, PersonaProfileInput } from '../../types/transform';
import type { SicResult } from '../../types/analysis';
import type { SplitContent } from '../workspace/workspace-types';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface TransformToolProps {
  selectedContentIds?: string[];
  onTransformApplied?: (contentId: string, transformedContent: string) => void;
  /** Signal workspace to enter split-pane mode */
  onSplitRequested?: (content: SplitContent) => void;
  /** SIC analysis results for subjective authenticity guidance */
  sicData?: SicResult | null;
  className?: string;
}

type TransformType = 'humanize' | 'persona' | 'style' | 'summarize';
type TransformStatus = 'idle' | 'previewing' | 'applying' | 'error';
type HumanizeIntensity = 'light' | 'moderate' | 'aggressive';

const GENRE_OPTIONS: Array<{ id: HumanizeGenre; label: string }> = [
  { id: 'general', label: 'General' },
  { id: 'academic', label: 'Academic' },
  { id: 'casual', label: 'Casual' },
  { id: 'technical', label: 'Technical' },
  { id: 'creative', label: 'Creative' },
  { id: 'journalistic', label: 'Journalistic' },
];

// ═══════════════════════════════════════════════════════════════════════════
// SIGNAL ROW BUILDER (dynamic — shows v3.1 signals when present)
// ═══════════════════════════════════════════════════════════════════════════

type SignalRow = [label: string, before: number, after: number, higherIsBetter: boolean];

function buildSignalRows(before: DetectionSignalSnapshot, after: DetectionSignalSnapshot): SignalRow[] {
  const rows: SignalRow[] = [
    // Core 5 signals (always present)
    ['Burstiness', before.burstiness, after.burstiness, true],
    ['Tell Phrases', before.tellPhraseScore, after.tellPhraseScore, false],
    ['N-gram Diversity', before.ngramDiversity, after.ngramDiversity, true],
    ['Semicolons', before.semicolonRate, after.semicolonRate, false],
    ['Complexity', before.sentenceComplexity, after.sentenceComplexity, true],
  ];

  // v3.1 expanded signals — show only when present
  if (before.hapaxLegomenaRate != null && after.hapaxLegomenaRate != null) {
    rows.push(['Hapax Rate', before.hapaxLegomenaRate, after.hapaxLegomenaRate, true]);
  }
  if (before.ttrWindowed != null && after.ttrWindowed != null) {
    rows.push(['Vocab TTR', before.ttrWindowed, after.ttrWindowed, true]);
  }
  if (before.contractionDensity != null && after.contractionDensity != null) {
    rows.push(['Contractions', before.contractionDensity, after.contractionDensity, true]);
  }
  if (before.compressionRatio != null && after.compressionRatio != null) {
    rows.push(['Compression', before.compressionRatio, after.compressionRatio, true]);
  }
  if (before.sentenceLengthKurtosis != null && after.sentenceLengthKurtosis != null) {
    rows.push(['Length Kurtosis', before.sentenceLengthKurtosis, after.sentenceLengthKurtosis, true]);
  }
  if (before.punctuationContrast != null && after.punctuationContrast != null) {
    rows.push(['Punct. Contrast', before.punctuationContrast, after.punctuationContrast, true]);
  }
  if (before.embeddingTrajectory != null && after.embeddingTrajectory != null) {
    rows.push(['Embedding Flow', before.embeddingTrajectory, after.embeddingTrajectory, false]);
  }

  return rows;
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function TransformTool({
  selectedContentIds = [],
  onTransformApplied,
  onSplitRequested,
  sicData,
  className = '',
}: TransformToolProps): React.ReactElement {
  const { workingContent, transformAndCommit, selectedText, focusedText } = useBufferSync();
  const api = useApi();
  const transformApi = useTransformApi();

  // Core state
  const [transformType, setTransformType] = useState<TransformType | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [status, setStatus] = useState<TransformStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  // Scope: current message vs all content
  const [useAllContent, setUseAllContent] = useState(false);

  // Humanize options
  const [intensity, setIntensity] = useState<HumanizeIntensity>('moderate');
  const [genre, setGenre] = useState<HumanizeGenre>('general');
  const [useClosedLoop, setUseClosedLoop] = useState(true);
  const [detectionComparison, setDetectionComparison] = useState<DetectionComparison | null>(null);
  const [summarizeStyle, setSummarizeStyle] = useState<'brief' | 'detailed'>('brief');

  // Persona/Style options — pre-populated with fallbacks to avoid flash of empty state
  const [personas, setPersonas] = useState<PresetInfo[]>(() =>
    FALLBACK_PERSONAS.map(p => ({ ...p, description: p.description || '', isSystem: true }))
  );
  const [styles, setStyles] = useState<PresetInfo[]>(() =>
    FALLBACK_STYLES.map(s => ({ ...s, description: s.description || '', isSystem: true }))
  );
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [customPrompt, setCustomPrompt] = useState('');
  const [useCustom, setUseCustom] = useState(false);

  // Profile factory state
  const [editingPreset, setEditingPreset] = useState<PresetInfo | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editForm, setEditForm] = useState<PersonaProfileInput>({
    name: '',
    description: '',
    voiceTraits: [],
    toneMarkers: [],
    formalityRange: [0.3, 0.7],
    referenceExamples: [],
  });
  const [editSaving, setEditSaving] = useState(false);

  // Suggested intensity from detection (Electron only)
  const [suggestedIntensity, setSuggestedIntensity] = useState<'subtle' | 'moderate' | 'strong' | null>(null);

  // Abort ref for cancellation
  const abortRef = React.useRef(false);

  const hasContent = workingContent.length > 0 || !!focusedText;
  const fullText = useMemo(
    () => workingContent.map((item) => item.text).join('\n\n'),
    [workingContent]
  );
  // Priority: selected text > focused message > full buffer
  const defaultText = focusedText && !useAllContent ? focusedText : fullText;
  const contentText = selectedText || defaultText;

  // Upgrade presets from API when persona/style is selected (fallbacks already shown)
  const [personasLoaded, setPersonasLoaded] = useState(false);
  const [stylesLoaded, setStylesLoaded] = useState(false);
  useEffect(() => {
    if (transformType === 'persona' && !personasLoaded) {
      setPersonasLoaded(true);
      transformApi.listPersonas().then((r) => {
        if (r.personas.length > 0) {
          setPersonas(r.personas.map(p => ({ ...p, description: p.description || '' })));
        }
      }).catch(() => {});
    }
    if (transformType === 'style' && !stylesLoaded) {
      setStylesLoaded(true);
      transformApi.listStyles().then((r) => {
        if (r.styles.length > 0) {
          setStyles(r.styles.map(s => ({ ...s, description: s.description || '' })));
        }
      }).catch(() => {});
    }
  }, [transformType, transformApi, personasLoaded, stylesLoaded]);

  const canTransform = useMemo(() => {
    if (!hasContent || !transformType) return false;
    if (transformType === 'humanize' || transformType === 'summarize') return true;
    if (useCustom) return customPrompt.trim().length > 0;
    return selectedPresetId !== null;
  }, [hasContent, transformType, intensity, useCustom, customPrompt, selectedPresetId]);

  const handleSelectType = useCallback((type: TransformType) => {
    setTransformType(type);
    setPreview(null);
    setError(null);
    setSelectedPresetId(null);
    setUseCustom(false);
    setCustomPrompt('');
    setDetectionComparison(null);
    setSuggestedIntensity(null);
  }, []);

  const handlePreview = useCallback(async () => {
    if (!canTransform || !transformType) return;

    // Check availability for LLM-based transforms
    if (['humanize', 'persona', 'style', 'summarize'].includes(transformType) && !transformApi.isAvailable) {
      setError(transformApi.unavailableReason || 'Transform not available');
      return;
    }

    setStatus('previewing');
    setError(null);

    try {
      let resultText: string | undefined;

      switch (transformType) {
        case 'humanize': {
          // Extract SIC feature scores for subjective authenticity guidance
          const sicFeatures = sicData?.features;
          if (useClosedLoop) {
            const result = await transformApi.humanizeWithDetection(contentText, intensity, genre, sicFeatures);
            resultText = result.text;
            setDetectionComparison(result.detection);
            setSuggestedIntensity(result.suggestedIntensity ?? null);
          } else {
            const result = await transformApi.humanize(contentText, intensity, genre, sicFeatures);
            resultText = result.text;
            setDetectionComparison(null);
            setSuggestedIntensity(result.suggestedIntensity ?? null);
          }
          break;
        }
        case 'persona': {
          if (useCustom) {
            const result = await transformApi.personaCustom(contentText, 'Custom', customPrompt);
            resultText = result.text;
          } else {
            if (!selectedPresetId) break;
            const result = await transformApi.persona(contentText, selectedPresetId);
            resultText = result.text;
          }
          break;
        }
        case 'style': {
          if (useCustom) {
            const result = await transformApi.styleCustom(contentText, 'Custom', customPrompt);
            resultText = result.text;
          } else {
            if (!selectedPresetId) break;
            const result = await transformApi.style(contentText, selectedPresetId);
            resultText = result.text;
          }
          break;
        }
        case 'summarize': {
          const result = await transformApi.summarize(contentText, summarizeStyle);
          resultText = result.text;
          break;
        }
      }

      if (resultText) setPreview(resultText);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Preview failed');
    } finally {
      setStatus('idle');
    }
  }, [canTransform, transformType, contentText, intensity, useCustom, customPrompt, selectedPresetId, transformApi]);

  const handleApply = useCallback(async () => {
    if (!canTransform || !transformType || !preview) return;

    setStatus('applying');
    setError(null);

    try {
      const originalText = contentText;

      // Build params for transformAndCommit
      const params: Record<string, unknown> = {};
      if (transformType === 'humanize') {
        params.intensity = intensity;
      } else if (useCustom) {
        const key = transformType === 'persona' ? 'customPersona' : 'customStyle';
        const promptKey = transformType === 'persona' ? 'systemPrompt' : 'stylePrompt';
        params[key] = { name: 'Custom', [promptKey]: customPrompt };
      } else {
        params.presetId = selectedPresetId;
      }

      // Pass preview text so Apply doesn't re-run the LLM
      params.precomputedText = preview;

      await transformAndCommit(transformType, params);

      for (const contentId of selectedContentIds) {
        onTransformApplied?.(contentId, preview);
      }

      // Signal workspace to enter split-pane mode
      onSplitRequested?.({ original: originalText, transformed: preview });

      setPreview(null);
      setTransformType(null);
      setCustomPrompt('');
      setSelectedPresetId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Apply failed');
    } finally {
      setStatus('idle');
    }
  }, [canTransform, transformType, preview, contentText, intensity, useCustom, customPrompt, selectedPresetId, selectedContentIds, onTransformApplied, onSplitRequested, transformAndCommit]);

  const handleCancel = useCallback(() => {
    setPreview(null);
    setError(null);
    setDetectionComparison(null);
  }, []);

  const reloadPersonas = useCallback(() => {
    api.transform.listPersonas().then((r) => setPersonas(r.personas)).catch(() => {});
  }, [api]);

  const handleEditPreset = useCallback((preset: PresetInfo) => {
    setEditingPreset(preset);
    setShowCreateForm(false);
    setEditForm({
      name: preset.name,
      description: preset.description,
    });
  }, []);

  const handleCreateNew = useCallback(() => {
    setEditingPreset(null);
    setShowCreateForm(true);
    setEditForm({
      name: '',
      description: '',
      voiceTraits: [],
      toneMarkers: [],
      formalityRange: [0.3, 0.7],
      referenceExamples: [],
    });
  }, []);

  const handleSavePersona = useCallback(async () => {
    setEditSaving(true);
    try {
      if (editingPreset) {
        await api.personas.update(editingPreset.id, editForm);
      } else {
        await api.personas.create({ ...editForm, name: editForm.name || 'New Persona' });
      }
      reloadPersonas();
      setEditingPreset(null);
      setShowCreateForm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save persona');
    } finally {
      setEditSaving(false);
    }
  }, [editingPreset, editForm, api, reloadPersonas]);

  const handleShadowPreset = useCallback(async (preset: PresetInfo) => {
    try {
      await api.personas.shadow(preset.id);
      reloadPersonas();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create shadow');
    }
  }, [api, reloadPersonas]);

  const handleResetPreset = useCallback(async (preset: PresetInfo) => {
    if (!preset.parentId) return;
    try {
      await api.personas.reset(preset.parentId);
      reloadPersonas();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset');
    }
  }, [api, reloadPersonas]);

  const handleDeletePreset = useCallback(async (preset: PresetInfo) => {
    try {
      await api.personas.delete(preset.id);
      reloadPersonas();
      if (selectedPresetId === preset.id) setSelectedPresetId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  }, [api, reloadPersonas, selectedPresetId]);

  const handleCancelEdit = useCallback(() => {
    setEditingPreset(null);
    setShowCreateForm(false);
  }, []);

  const presets = transformType === 'persona' ? personas : styles;

  return (
    <div className={`transform-tool ${className}`}>
      {/* Unavailability Banner */}
      {!transformApi.isAvailable && (
        <div className="transform-tool__unavailable" role="alert">
          <span aria-hidden="true">{'\u26A0\uFE0F'}</span>
          <span>{transformApi.unavailableReason || 'Transforms unavailable'}</span>
          {transformApi.backend !== 'none' && (
            <span className="transform-tool__backend-badge">{transformApi.backend}</span>
          )}
        </div>
      )}

      {/* Selection Status */}
      <div className="transform-tool__selection">
        {hasContent ? (
          <span className="transform-tool__selection-count">
            {workingContent.length} item{workingContent.length !== 1 ? 's' : ''} in workspace
            ({contentText.split(/\s+/).filter(Boolean).length} words)
          </span>
        ) : (
          <span className="transform-tool__selection-empty">
            Select content to transform
          </span>
        )}
      </div>

      {/* Source Indicator + Scope Toggle */}
      {hasContent && (
        <div className="transform-tool__source-indicator">
          {selectedText
            ? `Selection (${selectedText.split(/\s+/).filter(Boolean).length} words)`
            : focusedText && !useAllContent
              ? `Current message (${focusedText.split(/\s+/).filter(Boolean).length} words)`
              : `Full buffer (${fullText.split(/\s+/).filter(Boolean).length} words)`}
          {focusedText && !selectedText && (
            <button
              className="transform-tool__scope-toggle"
              onClick={() => setUseAllContent(v => !v)}
              title={useAllContent ? 'Transform current message only' : 'Transform all messages'}
            >
              {useAllContent ? '← Current msg' : 'All messages →'}
            </button>
          )}
        </div>
      )}

      {/* Transform Type Selector */}
      <div className="transform-tool__options">
        <div className="transform-tool__options-header">Transformation Type</div>
        <div className="transform-tool__options-grid" role="listbox" aria-label="Transformation options">
          {([
            { id: 'humanize' as const, label: 'Humanize', icon: '\u{1F4AB}', desc: 'Make AI text sound more natural' },
            { id: 'persona' as const, label: 'Persona', icon: '\u{1F464}', desc: 'Transform through a persona lens' },
            { id: 'style' as const, label: 'Style', icon: '\u270D\uFE0F', desc: 'Rewrite in a specific prose style' },
            { id: 'summarize' as const, label: 'Summarize', icon: '\u{1F4DD}', desc: 'Generate a summary of the content' },
          ]).map((opt) => (
            <button
              key={opt.id}
              className={`transform-option ${transformType === opt.id ? 'transform-option--selected' : ''}`}
              onClick={() => handleSelectType(opt.id)}
              disabled={!hasContent}
              role="option"
              aria-selected={transformType === opt.id}
            >
              <span className="transform-option__icon" aria-hidden="true">{opt.icon}</span>
              <span className="transform-option__label">{opt.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Humanize: Intensity + Genre + Closed-Loop */}
      {transformType === 'humanize' && (
        <>
          <div className="transform-tool__intensity">
            <div className="transform-tool__intensity-header">Intensity</div>
            <div className="transform-tool__intensity-options">
              {(['light', 'moderate', 'aggressive'] as const).map((level) => (
                <label key={level} className="transform-tool__intensity-label">
                  <input
                    type="radio"
                    name="intensity"
                    value={level}
                    checked={intensity === level}
                    onChange={() => setIntensity(level)}
                    className="transform-tool__intensity-radio"
                  />
                  <span className="transform-tool__intensity-text">{level}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="transform-tool__intensity">
            <div className="transform-tool__intensity-header">Genre</div>
            <div className="transform-tool__intensity-options">
              {GENRE_OPTIONS.map((g) => (
                <label key={g.id} className="transform-tool__intensity-label">
                  <input
                    type="radio"
                    name="genre"
                    value={g.id}
                    checked={genre === g.id}
                    onChange={() => setGenre(g.id)}
                    className="transform-tool__intensity-radio"
                  />
                  <span className="transform-tool__intensity-text">{g.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="transform-tool__intensity">
            <label className="transform-tool__intensity-label">
              <input
                type="checkbox"
                checked={useClosedLoop}
                onChange={(e) => setUseClosedLoop(e.target.checked)}
                className="transform-tool__intensity-radio"
              />
              <span className="transform-tool__intensity-text">Detect + Humanize + Re-detect</span>
            </label>
          </div>
        </>
      )}

      {/* Summarize: Style Selector */}
      {transformType === 'summarize' && (
        <div className="transform-tool__intensity">
          <div className="transform-tool__intensity-header">Summary Style</div>
          <div className="transform-tool__intensity-options">
            {(['brief', 'detailed'] as const).map((s) => (
              <label key={s} className="transform-tool__intensity-label">
                <input
                  type="radio"
                  name="summarize-style"
                  value={s}
                  checked={summarizeStyle === s}
                  onChange={() => setSummarizeStyle(s)}
                  className="transform-tool__intensity-radio"
                />
                <span className="transform-tool__intensity-text">{s}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Persona/Style: Preset Grid */}
      {(transformType === 'persona' || transformType === 'style') && (
        <div className="transform-tool__presets">
          <div className="transform-tool__presets-header">
            {transformType === 'persona' ? 'Persona' : 'Style'} Presets
          </div>
          <div className="transform-tool__presets-grid">
            {presets.map((preset) => {
              const isSelected = selectedPresetId === preset.id && !useCustom;
              const presetClass = [
                'transform-tool__preset',
                isSelected ? 'transform-tool__preset--selected' : '',
                preset.isUserVoice ? 'transform-tool__preset--voice' : '',
                preset.isUserOverride ? 'transform-tool__preset--shadow' : '',
                !preset.isSystem && !preset.isUserVoice && !preset.isUserOverride && preset.id !== 'user-voice'
                  ? 'transform-tool__preset--custom' : '',
              ].filter(Boolean).join(' ');

              return (
                <div key={preset.id} className="transform-tool__preset-wrapper">
                  <button
                    className={presetClass}
                    onClick={() => { setSelectedPresetId(preset.id); setUseCustom(false); }}
                    title={preset.description}
                  >
                    {preset.isUserVoice && <span className="transform-tool__voice-badge">YOU</span>}
                    {preset.isUserOverride && <span className="transform-tool__shadow-icon" aria-label="User override" title="Your customization of a system persona">{'\u270E'}</span>}
                    {!preset.isSystem && !preset.isUserVoice && !preset.isUserOverride && preset.id !== 'user-voice' && (
                      <span className="transform-tool__custom-icon" aria-label="Custom persona" title="Your custom persona">{'\u2605'}</span>
                    )}
                    {preset.name}
                  </button>
                  {/* Edit icon for non-system, non-voice presets */}
                  {transformType === 'persona' && !preset.isSystem && preset.id !== 'user-voice' && (
                    <button
                      className="transform-tool__preset-edit-icon"
                      onClick={(e) => { e.stopPropagation(); handleEditPreset(preset); }}
                      title="Edit persona"
                      aria-label={`Edit ${preset.name}`}
                    >
                      {'\u270E'}
                    </button>
                  )}
                  {/* Shadow edit for system presets */}
                  {transformType === 'persona' && preset.isSystem && !preset.isUserOverride && (
                    <button
                      className="transform-tool__preset-edit-icon"
                      onClick={(e) => { e.stopPropagation(); handleShadowPreset(preset); }}
                      title="Customize this persona"
                      aria-label={`Customize ${preset.name}`}
                    >
                      {'\u270E'}
                    </button>
                  )}
                </div>
              );
            })}
            <button
              className={`transform-tool__preset ${useCustom ? 'transform-tool__preset--selected' : ''}`}
              onClick={() => { setUseCustom(true); setSelectedPresetId(null); }}
            >
              Custom
            </button>
            {transformType === 'persona' && (
              <button
                className="transform-tool__preset transform-tool__preset--create"
                onClick={handleCreateNew}
                title="Create a new persona"
              >
                + New
              </button>
            )}
          </div>

          {/* Edit Panel */}
          {(editingPreset || showCreateForm) && transformType === 'persona' && (
            <div className="transform-tool__edit-panel">
              <div className="transform-tool__edit-panel-header">
                {editingPreset ? `Edit: ${editingPreset.name}` : 'New Persona'}
              </div>
              <label className="transform-tool__edit-label">
                Name
                <input
                  className="transform-tool__edit-input"
                  value={editForm.name}
                  onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Persona name"
                />
              </label>
              <label className="transform-tool__edit-label">
                Description
                <textarea
                  className="transform-tool__edit-textarea"
                  value={editForm.description ?? ''}
                  onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Describe the persona's worldview, epistemics, and voice..."
                  rows={3}
                />
              </label>
              <label className="transform-tool__edit-label">
                Voice Traits (comma-separated)
                <input
                  className="transform-tool__edit-input"
                  value={(editForm.voiceTraits ?? []).join(', ')}
                  onChange={(e) => setEditForm((f) => ({ ...f, voiceTraits: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) }))}
                  placeholder="e.g., precise, warm, measured"
                />
              </label>
              <label className="transform-tool__edit-label">
                Tone Markers (comma-separated)
                <input
                  className="transform-tool__edit-input"
                  value={(editForm.toneMarkers ?? []).join(', ')}
                  onChange={(e) => setEditForm((f) => ({ ...f, toneMarkers: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) }))}
                  placeholder="e.g., reflective, analytical"
                />
              </label>
              <label className="transform-tool__edit-label">
                Reference Examples (paste samples, one per line)
                <textarea
                  className="transform-tool__edit-textarea"
                  value={(editForm.referenceExamples ?? []).join('\n\n')}
                  onChange={(e) => setEditForm((f) => ({ ...f, referenceExamples: e.target.value.split('\n\n').filter(Boolean) }))}
                  placeholder="Paste writing samples that exemplify this voice..."
                  rows={4}
                />
              </label>
              <div className="transform-tool__edit-actions">
                {editingPreset?.isUserOverride && (
                  <button
                    className="transform-tool__btn transform-tool__btn--warning"
                    onClick={() => { handleResetPreset(editingPreset); handleCancelEdit(); }}
                  >
                    Reset to System Default
                  </button>
                )}
                {editingPreset && !editingPreset.isSystem && !editingPreset.isUserVoice && (
                  <button
                    className="transform-tool__btn transform-tool__btn--danger"
                    onClick={() => { handleDeletePreset(editingPreset); handleCancelEdit(); }}
                  >
                    Delete
                  </button>
                )}
                <button className="transform-tool__btn transform-tool__btn--secondary" onClick={handleCancelEdit}>
                  Cancel
                </button>
                <button
                  className="transform-tool__btn transform-tool__btn--primary"
                  onClick={handleSavePersona}
                  disabled={editSaving || !editForm.name}
                >
                  {editSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          )}

          {useCustom && (
            <div className="transform-tool__custom">
              <label htmlFor="transform-custom-prompt" className="transform-tool__custom-label">
                {transformType === 'persona' ? 'System Prompt' : 'Style Prompt'}
              </label>
              <textarea
                id="transform-custom-prompt"
                className="transform-tool__custom-input"
                placeholder={transformType === 'persona'
                  ? 'Describe the persona\'s worldview, epistemics, and voice...'
                  : 'Describe sentence patterns, vocabulary, and rhetoric...'}
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                rows={3}
              />
            </div>
          )}
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="transform-tool__error" role="alert">
          <span aria-hidden="true">{'\u26A0\uFE0F'}</span>
          {error}
        </div>
      )}

      {/* Detection Comparison (shown after closed-loop humanize) */}
      {detectionComparison && (
        <div className="transform-tool__detection-comparison">
          <div className="transform-tool__detection-header">
            AI Detection: {(detectionComparison.before.aiLikelihood > 1 ? detectionComparison.before.aiLikelihood : detectionComparison.before.aiLikelihood * 100).toFixed(0)}%
            {' \u2192 '}
            {(detectionComparison.after.aiLikelihood > 1 ? detectionComparison.after.aiLikelihood : detectionComparison.after.aiLikelihood * 100).toFixed(0)}%
            {detectionComparison.improved ? ' \u2713' : ' \u2717'}
          </div>
          <div className="transform-tool__detection-signals">
            {buildSignalRows(detectionComparison.before, detectionComparison.after).map(([label, before, after, higherIsBetter]) => {
              const improved = higherIsBetter ? after > before : after < before;
              return (
                <div key={label} className="transform-tool__detection-signal">
                  <span className="transform-tool__signal-label">{label}</span>
                  <span className="transform-tool__signal-values">
                    {before.toFixed(2)} {'\u2192'} {after.toFixed(2)}
                    {' '}
                    <span className={improved ? 'transform-tool__signal-improved' : 'transform-tool__signal-worse'}>
                      {improved ? '\u2713' : '\u2717'}
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
          {!detectionComparison.improved && (
            <button
              className="transform-tool__btn transform-tool__btn--secondary"
              onClick={handlePreview}
              disabled={status === 'previewing'}
            >
              Re-humanize
            </button>
          )}
        </div>
      )}

      {/* Intensity Suggestion (Electron only, shown after humanize) */}
      {suggestedIntensity && transformType === 'humanize' && (() => {
        const currentMapped = intensity === 'light' ? 'subtle' : intensity === 'aggressive' ? 'strong' : 'moderate';
        if (suggestedIntensity === currentMapped) return null;
        const labelMap: Record<string, string> = { subtle: 'light', moderate: 'moderate', strong: 'aggressive' };
        return (
          <div className="transform-tool__suggestion">
            Detection suggests <strong>{labelMap[suggestedIntensity]}</strong> intensity (currently {intensity}).
            <button
              className="transform-tool__suggestion-btn"
              onClick={() => {
                const mapped = suggestedIntensity === 'subtle' ? 'light' : suggestedIntensity === 'strong' ? 'aggressive' : 'moderate';
                setIntensity(mapped as HumanizeIntensity);
                setSuggestedIntensity(null);
              }}
            >
              Apply
            </button>
          </div>
        );
      })()}

      {/* Preview */}
      {preview && (
        <div className="transform-tool__preview">
          <div className="transform-tool__preview-header">
            <span>Preview</span>
            <button
              className="transform-tool__preview-close"
              onClick={handleCancel}
              aria-label="Close preview"
            >
              ×
            </button>
          </div>
          <div className="transform-tool__preview-content">{preview}</div>
        </div>
      )}

      {/* Actions */}
      <div className="transform-tool__actions">
        {preview ? (
          <>
            <button
              className="transform-tool__btn transform-tool__btn--secondary"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              className="transform-tool__btn transform-tool__btn--primary"
              onClick={handleApply}
              disabled={status === 'applying'}
            >
              {status === 'applying' ? 'Applying...' : 'Apply'}
            </button>
          </>
        ) : status === 'previewing' ? (
          <button
            className="transform-tool__btn transform-tool__btn--cancel"
            onClick={() => { abortRef.current = true; setStatus('idle'); setError(null); }}
          >
            Cancel
          </button>
        ) : (
          <button
            className="transform-tool__btn transform-tool__btn--primary"
            onClick={handlePreview}
            disabled={!canTransform}
          >
            Preview
          </button>
        )}
      </div>

      {/* Placeholder */}
      {!hasContent && (
        <div className="transform-tool__placeholder">
          <span aria-hidden="true">{'\u2728'}</span>
          <p>No content in workspace</p>
          <p className="transform-tool__placeholder-hint">
            Use the archive browser or search to add content to the workspace
          </p>
        </div>
      )}
    </div>
  );
}

export default TransformTool;
